import {
  QuestionRuleInstance,
  RuleAction,
  RuleActionType,
} from '@jupiterone/jupiter-types';
import { ChangeEvent, useEffect, useState } from 'react';
import { RuleActionData } from 'jupiter-web-alerts/src/types';
import get from 'lodash/get';
import { QuestionRuleInstanceWithTags } from '@jupiterone/jupiter-web-toolkit';

export const ADDITIONAL_ACTIONS: RuleActionType[] = [
  RuleActionType.SEND_EMAIL,
  RuleActionType.SEND_SLACK_MESSAGE,
  RuleActionType.CREATE_JIRA_TICKET,
  RuleActionType.CREATE_SERVICE_NOW_INCIDENT,
  RuleActionType.PUBLISH_SNS_MESSAGE,
  RuleActionType.SEND_SQS_MESSAGE,
  RuleActionType.WEBHOOK,
];

export interface HookProps {
  rule: QuestionRuleInstanceWithTags;
  onChange: (editedRule: QuestionRuleInstanceWithTags) => void;
}

const useBasicRuleEditor = ({ rule, onChange }: HookProps) => {
  const [actionData, setActionData] = useState<{ [key: string]: any }>({
    SEND_SLACK_MESSAGE: {},
    SEND_EMAIL: {},
    CREATE_JIRA_TICKET: {},
    CREATE_SERVICE_NOW_INCIDENT: {},
    SEND_SQS_MESSAGE: {},
    PUBLISH_SNS_MESSAGE: {},
    WEBHOOK: {},
  });
  const [selectedActions, setSelectedActions] = useState<RuleActionType[]>([]);

  useEffect(() => {
    setSelectedActions(
      getRuleActions(rule)
        .filter((a) => ADDITIONAL_ACTIONS.indexOf(a.type) >= 0)
        .map((a) => a.type)
    );
  }, [rule]);

  const updateAction = (
    previousActions: RuleAction[],
    update: Partial<RuleAction>
  ) => {
    const indexToUpdate = previousActions.findIndex(
      (a) => a.type === update.type
    );
    if (indexToUpdate === -1) {
      return [...previousActions, update];
    }

    const newActions = [...previousActions];
    newActions[indexToUpdate] = {
      ...newActions[indexToUpdate],
      ...update,
    } as RuleAction;
    return newActions;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'query':
        onChange({
          ...rule,
          question: {
            queries: [
              {
                ...(rule?.question?.queries?.[0] || {}),
                query: e.target.value,
              },
            ],
          },
        } as any);
        break;
      case 'severity':
        // Cast to any because jupiter-types doesn't have full support for all fields in set_property
        onChange({
          ...rule,
          operations: [
            {
              ...get(rule, 'operations[0]'),
              actions: updateAction(rule.operations?.[0]?.actions ?? [], {
                type: RuleActionType.SET_PROPERTY,
                targetProperty: 'alertLevel',
                targetValue: e.target.value,
              } as any),
            },
          ],
        } as any);
        break;
      default:
        onChange({ ...rule, [e.target.name]: e.target.value } as any);
    }
  };

  const updateRuleActions = (actions: RuleActionData[]) =>
    onChange({
      ...rule,
      operations: [
        {
          actions,
          when: get(rule, 'operations[0].when', {}),
        },
      ],
    } as any);

  const handleActionChange = (e: ChangeEvent<{ value: unknown }>) => {
    const action: any = e.target.value;
    const newActions =
      selectedActions.indexOf(action) >= 0
        ? selectedActions.filter((a) => a !== action)
        : [...selectedActions, action];
    const existingActions = getRuleActions(rule).filter(
      (action: { type: RuleActionType }) =>
        ADDITIONAL_ACTIONS.indexOf(action.type) === -1
    );
    setSelectedActions(newActions);
    const newRuleActions = [
      ...existingActions,
      ...newActions.map((type) => {
        // If the action is being re enabled, reload the previous values into the rule
        const existingAction =
          getRuleActions(rule).find((a: RuleActionData) => a.type === type) ||
          actionData[e.target.value as string] ||
          {};
        return { ...existingAction, type };
      }),
    ];
    updateRuleActions(newRuleActions);
  };

  const handleUpdateAction = (newValue: RuleActionData) => {
    const actions = [...getRuleActions(rule)];
    const index = actions.findIndex(
      (r: RuleActionData) => r.type === newValue.type
    );
    if (index >= 0) {
      actions[index] = newValue;
    } else {
      actions.push(newValue);
    }
    const newActionData = { ...actionData, [newValue.type]: newValue };
    setActionData(newActionData);
    if (selectedActions.indexOf(newValue.type) >= 0) {
      updateRuleActions(actions);
    }
  };

  const getInitialActionData = (action: RuleActionType) => {
    const actions = [...getRuleActions(rule)];
    const index = actions.findIndex((r: RuleActionData) => r.type === action);
    if (index >= 0) {
      return actions[index];
    } else {
      return undefined;
    }
  };
  return {
    selectedActions,
    handleActionChange,
    handleChange,
    handleUpdateAction,
    getInitialActionData,
  };
};

const getRuleActions = (rule: Partial<QuestionRuleInstance>) =>
  get(rule, 'operations[0].actions', []) as RuleActionData[];

export default useBasicRuleEditor;
